import { stripAfterColon } from "../stripAfterColon/stripAfterColon";

export interface RawFilterOption {
	/** The underlying id sent to the backend (e.g. zone layout id, inspection type id). */
	id: string;
	/** The translated label, which may carry a ":<build/version>" suffix. */
	translation: string;
}

export interface GroupedFilterOption {
	/** The colon-stripped label shown to the user. */
	label: string;
	/** Comma-joined list of every id that maps to this label. */
	value: string;
}

/**
 * Group filter options by their colon-stripped translation.
 *
 * Multiple entries whose translations strip to the same label collapse into a
 * single option. The collapsed option's `value` is the comma-joined,
 * de-duplicated, sorted list of all the underlying ids in that group, e.g.:
 *
 *   [{ id: "ffc6...", translation: "BUS PRE-TRIP: ABC1234-3.2.1" },
 *    { id: "77b9...", translation: "BUS PRE-TRIP: QWE9876-4.3.2" }]
 *
 *   → [{ label: "BUS PRE-TRIP", value: "77b9...,ffc6..." }]
 *
 * Selecting "BUS PRE-TRIP" therefore lets the caller split the value back into
 * every id it represents (see the comma-split handlers in filter-card). IDs are
 * sorted so the joined value is stable across reloads regardless of source
 * ordering — important for the multi-select dropdown's persisted selection state.
 */
export const groupOptionsByStrippedLabel = (options: RawFilterOption[]): GroupedFilterOption[] => {
	const groups = new Map<string, Set<string>>();

	for (const { id, translation } of options) {
		const label = stripAfterColon(translation);
		if (!groups.has(label)) {
			groups.set(label, new Set<string>());
		}
		groups.get(label).add(id);
	}

	return Array.from(groups.entries()).map(([label, ids]) => ({
		label,
		value: Array.from(ids).sort().join(","),
	}));
};
